import { initializeApp } from "firebase/app";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { DEV_CONFIG } from "./dev/firebaseConfig";

const STG_CONFIG = {
  apiKey: "AIzaSyAHBrDimaOJyZVGx1zA1-pblRoMPnFKxsM",
  authDomain: "due-stg.firebaseapp.com",
  projectId: "due-stg",
  storageBucket: "due-stg.firebasestorage.app",
  messagingSenderId: "1009355885797",
  appId: "1:1009355885797:web:b565c2c91a4ce1b4eb980c",
  measurementId: "G-2VT06B104M",
};

const PRD_CONFIG = {
  apiKey: "AIzaSyAeuYWitJKANqEm4if-5XtykxXiROsERjY",
  authDomain: "due-prd.firebaseapp.com",
  projectId: "due-prd",
  storageBucket: "due-prd.appspot.com",
  messagingSenderId: "1002225243527",
  appId: "1:1002225243527:web:77272c01a6d1fbcc3f2f22",
  measurementId: "G-G4S1L4KJKZ",
};

const isPlaceholder = (value) => {
  const text = (value || "").toString().trim();
  return !text || text.includes("__");
};

// Ambiente do app. Só entra em "dev" com REACT_APP_ENVIRONMENT=dev explícito
// (ver .env.dev + `npm run start:dev`) — nunca por heurística de hostname.
// Isso é deliberado: no app enterprise, `localhost` caía em `stg` por
// fallback, então rodar local sempre falava com o due-stg REAL. Aqui o
// default continua stg pra não mudar o comportamento de quem já usa, mas o
// modo dev é opt-in explícito e inequívoco.
const ENVIRONMENT = (process.env.REACT_APP_ENVIRONMENT || "").toString().trim().toLowerCase();
const IS_DEV = ENVIRONMENT === "dev";

const resolveFallbackConfig = () => {
  const host = (window?.location?.hostname || "").toLowerCase();
  // Ordem importa: stg.eia.due.app.br também contém "eia.due.app.br".
  if (host.includes("stg.eia.due.app.br") || host.includes("due-stg")) return STG_CONFIG;
  if (host === "eia.due.app.br" || host.includes("due-prd")) return PRD_CONFIG;
  return STG_CONFIG;
};

const getFirebaseConfig = () => {
  // Em dev, a config do emulador vence qualquer variável de ambiente: não faz
  // sentido montar credencial de projeto real pra falar com 127.0.0.1.
  if (IS_DEV) return DEV_CONFIG;

  const fromEnv = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID,
    measurementId: process.env.REACT_APP_MEASUREMENT_ID
  };

  const fallback = resolveFallbackConfig();
  const mustFallback = isPlaceholder(fromEnv.projectId) || isPlaceholder(fromEnv.apiKey) || isPlaceholder(fromEnv.authDomain);
  if (!mustFallback) return fromEnv;

  return {
    apiKey: isPlaceholder(fromEnv.apiKey) ? fallback.apiKey : fromEnv.apiKey,
    authDomain: isPlaceholder(fromEnv.authDomain) ? fallback.authDomain : fromEnv.authDomain,
    projectId: isPlaceholder(fromEnv.projectId) ? fallback.projectId : fromEnv.projectId,
    storageBucket: isPlaceholder(fromEnv.storageBucket) ? fallback.storageBucket : fromEnv.storageBucket,
    messagingSenderId: isPlaceholder(fromEnv.messagingSenderId) ? fallback.messagingSenderId : fromEnv.messagingSenderId,
    appId: isPlaceholder(fromEnv.appId) ? fallback.appId : fromEnv.appId,
    measurementId: isPlaceholder(fromEnv.measurementId) ? fallback.measurementId : fromEnv.measurementId,
  };
};

const firebaseConfig = getFirebaseConfig();
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Ambiente due-dev: tudo passa pelos emuladores locais, nada sai da máquina.
// Portas espelham o bloco `emulators` de firebase.platform.json na raiz do
// repo. Rode `npm run dev:emulators` (raiz) antes de subir o app em modo dev.
//
// Sobre functions: este app não exporta uma instância compartilhada — cada
// módulo chama `getFunctions(app, 'southamerica-east1')` por conta própria
// (interviewService.js, avatarSpeechApi.js, mouthAssetsApi.js,
// DenunciaAvatarContainer.js...). O SDK memoiza por (app, região), então
// conectar aqui a instância dessa mesma região faz TODAS aquelas chamadas
// caírem no emulador, sem precisar tocar em cada arquivo.
if (IS_DEV) {
  connectAuthEmulator(auth, "http://127.0.0.1:9599", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8589);
  connectStorageEmulator(storage, "127.0.0.1", 9589);
  connectFunctionsEmulator(getFunctions(app, "southamerica-east1"), "127.0.0.1", 5589);
  // eslint-disable-next-line no-console
  console.info("[due-dev] Emuladores locais ativos — nenhum dado sai desta máquina.");
}
